import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Offcanvas } from "react-bootstrap";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { customStyles, today } from "../../Utils/helpers/commonhelper";
import { GetRequest, PostRequest } from "../../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const taskSchema = z.object({
  name: z.string().min(1, "Task name is required"),
  assignee: z.object(
    { value: z.string(), label: z.string() },
    { required_error: "Assignee is required" }
  ),
  dueDate: z.string().refine((date) => {
    const dt = new Date();
    dt.setHours(0, 0, 0, 0);
    return new Date(date) >= dt;
  }, "Due date cannot be in the past"),
  project: z.object({ value: z.string(), label: z.string() }).optional(),
  stage: z.object(
    { value: z.string(), label: z.string() },
    { required_error: "Stage is required" }
  ),
  description: z.string().optional(),
  docs: z.any().optional(),
});

const projectOptions = [
  { value: "p1", label: "Project A" },
  { value: "p2", label: "Project B" },
];

export const TasksOffcanvas = ({
  taskShow,
  onClose,
  setAddIndivisualTask,
  addIndivisbleTask,
  setEditTaskData,
  editTastData,
  setProjectId,
  projectId,
  fetchStages,
  setFetchTaskToken,
  fetchDetailsProjects,
  refetch,
  moduleName,
}) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  const customerId = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    authentication: rf,
    Authorization: token,
  };
  const extras = { customer, setCustomer, rf, navigate };

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [leadOwner, setLeadOwner] = useState([]);
  const [stages, setStages] = useState([]);
  const today = new Date().toISOString().split("T")[0];

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      stage: stages[0],
      dueDate: today,
    },
  });

  useEffect(() => {
    if (addIndivisbleTask) {
      const stageOp = {
        value: editTastData?.id,
        label: editTastData?.stage_name,
      };
      const projectOp = {
        value: editTastData?.project_id,
        label: editTastData?.project_title,
      };

      setValue("stage", stageOp);
      setValue("project", projectOp);
    }
  }, [addIndivisbleTask]);

  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setValue("docs", [...selectedFiles, ...newFiles]);
  };

  const removeFile = (idx) => {
    const updated = selectedFiles.filter((_, i) => i !== idx);
    setSelectedFiles(updated);
    setValue("docs", updated);
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        project_stage_id: data.stage?.value || "",
        task_name: data.name,
        owner_id: data.assignee?.value || "",
        due_date: data.dueDate,
        project_id: data.project?.value || "",
        description: data.description,
        attachment: data.docs,
        user_login_id: customer?.customer_details?.id,
      };

      const res = await PostRequest(
        `project/createTask`,
        payload,
        true,
        { headers },
        extras
      );

      if (res.status !== "success") {
        return toast.error(res.message || "Something went wrong");
      }

      reset();
      handleCloseTask();
      setSelectedFiles([]);
      await fetchDetailsProjects();
      await fetchStages();
      setFetchTaskToken((p) => !p);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchOwners = async () => {
    const res = await GetRequest(
      `users/users/fetchUserAsOwner/${customer.customer_details.customer_id}`,
      { headers },
      extras
    );

    if (res.err) return toast.error("Error fetching data");

    const owners = res.data.map((x) => ({
      value: x.id,
      label: x.full_name,
    }));
    setLeadOwner(owners)

    setLeadOwner(owners);

    const currentVal = getValues("assignee.value");
    if (!currentVal && owners.length > 0) {
      setValue("assignee", owners[0], { shouldValidate: true });
    }
  };

  useEffect(() => {
    fetchOwners();
  }, []);

  const fetchStageWithProjectId = async () => {
    if (!projectId) return;

    const res = await GetRequest(
      `project/fetchStages/${projectId}`,
      { headers },
      extras
    );

    if (res.status !== "success") return toast.error(res.message);

    const mapped = res.data.map((d) => ({
      value: d.id,
      label: d.stage_name,
    }));

    setStages(mapped);

    const projectOp = {
      value: res.data[0]?.project_id,
      label: res.data[0]?.project_title,
    };

    setValue("project", projectOp);
  };

  useEffect(() => {
    fetchStageWithProjectId();
  }, [projectId]);

  const handleCloseTask = () => {
    setEditTaskData([]);
    setAddIndivisualTask(false);

    reset({
      name: "",
      description: "",
      docs: [],

      assignee: leadOwner[0] ? leadOwner[0] : "",

      dueDate: today,

      stage: "",
    });

    setSelectedFiles([]);
    onClose();
  };

  return (
    <>
      <Offcanvas
        className="createContactOffcanvas"
        placement="end"
        backdrop="static"
        enforceFocus={true}
        show={taskShow}
        onHide={handleCloseTask}
      >
        {/* HEADER */}
        <Offcanvas.Header
          closeButton
          className="border-bottom border-dashed pt-3"
        >
          <div className="d-flex align-items-center gap-2">
            <div className="pt-0 ps-4">
              <Offcanvas.Title
                style={{ color: "#0791ff" }}
                className="addEnquiryHead"
              >
                {" "}
                Create Task
              </Offcanvas.Title>
              <p
                style={{ fontSize: "14px", color: "#b26792", lineHeight: 1.1 }}
                className=" text-secondary text-nowrap mb-0"
              ></p>
            </div>
          </div>
        </Offcanvas.Header>

        {/* BODY */}
        <Offcanvas.Body style={{ paddingTop: "0" }}>
          <div className="mb-5">
            <Form
              onSubmit={handleSubmit(onSubmit)}
              className=" form-container "
            >
              <Row className="w-100 ms-0 pt-3">
                <Col md={12}>
                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                    <Form.Label>
                      Task Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter task name"
                      {...register("name")}
                    />
                    {errors.name && (
                      <p className="text-danger small">{errors.name.message}</p>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* other fields → unchanged */}

              {/* --- Assignee --- */}
              <Row className="w-100 ms-0 pt-3">
                <Col md={6}>
                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                    <Form.Label>
                      Assignee <span className="text-danger">*</span>
                    </Form.Label>
                    <Controller
                      name="assignee"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={leadOwner}
                          styles={customStyles}
                          placeholder="Select Assignee"
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                    {errors.assignee && (
                      <p className="text-danger small">
                        {"Assignee is required"}
                      </p>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative ">
                    <Form.Label>
                      Due Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control type="date" {...register("dueDate")} />
                  </Form.Group>
                </Col>
              </Row>

              {/* Project + Stage */}
              <Row className="w-100 ms-0 pt-3">
                <Col md={6}>
                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                    <Form.Label>Project</Form.Label>
                    <Controller
                      name="project"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={projectOptions}
                          styles={customStyles}
                          isDisabled
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-2 d-flex flex-column focus-z-index position-relative ">
                    <Form.Label> Stage <span className="text-danger">*</span></Form.Label>
                    <Controller
                      name="stage"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={stages}
                          styles={customStyles}
                          isDisabled={addIndivisbleTask}
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                    {errors.stage && (
                      <p className="text-danger small">
                        {"Stage is required"}
                      </p>
                    )}                  </Form.Group>
                </Col>
              </Row>

              {/* Description */}
              <Row className="w-100 ms-0 pt-3">
                <Col md={12}>
                  <Form.Group className="mb-2 d-flex flex-column focus-z-index position-relative  ">
                    <Form.Label className="textareaLables">Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...register("description")}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Files */}
              <Row className="w-100 ms-0 pt-3">
                <Col md={12}>
                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative  ">
                    <Form.Label>Attach Documents</Form.Label>
                    <Form.Control
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                    />
                  </Form.Group>

                  {selectedFiles.length > 0 && (
                    <div className="mt-2">
                      <h6>Selected Files:</h6>
                      <div className="d-flex gap-2 flex-wrap">
                        {selectedFiles.map((file, i) => (
                          <div
                            key={i}
                            className="bg-light rounded px-2 py-1 d-flex align-items-center"
                          >
                            <span>{file.name}</span>
                            <Button
                              variant="link"
                              className="p-0 ms-2"
                              onClick={() => removeFile(i)}
                            >
                              <FontAwesomeIcon
                                icon={faXmark}
                                style={{ color: "#dc3545" }}
                              />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>

              {/* FOOTER FIXED */}
              <div
                style={{
                  position: "fixed",
                  width: "60%",
                  bottom: 0,
                  right: 0,
                  background: "#fff",
                  borderTop: "1px solid #ddd",
                  padding: "15px 20px",
                  zIndex: 3000,
                }}
              >
                <div className="d-flex justify-content-end gap-3">
                  <button className="btn btn-default-outline" onClick={handleCloseTask}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-default">
                    Save
                  </button> 
                </div>
              </div>
            </Form>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};
